import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Button, List, Typography, Spin, Alert, Tag, Input, Divider, Select, theme } from 'antd';
import {
  X,
  Folder,
  File,
  FileText,
  FileImage,
  Video,
  Volume2,
  FileArchive,
  FileSpreadsheet,
  Presentation,
  Eye,
  Download,
  Search,
  Globe,
  Book,
  ChevronRight,
} from 'lucide-react';
import Icon from './Icon';
import { useQuery } from '@tanstack/react-query';
import { getFilesByUserRole, getFileType, formatFileSize, canPreviewFile } from '../services/filesService';
import { searchToc } from '../services/fileIndexService';
import FileViewer from './FileViewer';
import { useThemeMode } from '../theme/ThemeProvider';

const { Text } = Typography;
const { useToken } = theme;

const MyFilesDialog = ({ open, onClose, userRole }) => {
  const { token } = useToken();
  const { mode } = useThemeMode();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const debounceTimer = useRef(null);

  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Fetch ALL files for the role once (or when role/open changes).
  // Search filtering happens client-side so it can also include TOC heading matches.
  useEffect(() => {
    if (open && userRole) {
      fetchFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userRole]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFilesByUserRole(userRole);
      setFiles(data.files || []);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // TOC search — runs alongside the name/desc filter when the user has typed ≥2 chars.
  const tocEnabled = searchTerm.trim().length >= 2;
  const { data: tocData, isFetching: tocFetching } = useQuery({
    queryKey: ['toc-search', searchTerm.trim()],
    queryFn: () => searchToc(searchTerm.trim(), 30),
    enabled: tocEnabled,
    staleTime: 60 * 1000,
  });

  // fileKey → matched TocEntry[]
  const tocHitsByKey = useMemo(() => {
    const map = {};
    (tocData?.results || []).forEach((r) => {
      map[r.fileKey] = r.matches;
    });
    return map;
  }, [tocData]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setSearchTerm(value), 400);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  const handleRefresh = () => fetchFiles();

  // Client-side filter: file is shown if its name / description / course matches,
  // OR if any of its TOC headings match. Empty search → show everything.
  const filteredFiles = useMemo(() => {
    const scopeFilteredFiles = files.filter((f) => {
      if (scopeFilter === 'all') return true;
      if (scopeFilter === 'global') return f.scope === 'global' || !f.scope;
      return f.scope === 'course';
    });

    if (!searchTerm) return scopeFilteredFiles;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return scopeFilteredFiles;
    return scopeFilteredFiles.filter((f) => {
      if (f.name?.toLowerCase().includes(q)) return true;
      if ((f.metadata?.description || '').toLowerCase().includes(q)) return true;
      if ((f.courseNames || []).some((n) => n.toLowerCase().includes(q))) return true;
      if (tocHitsByKey[f.key]) return true;
      return false;
    });
  }, [files, searchTerm, scopeFilter, tocHitsByKey]);

  // Group: global → course groups (by courseNames from BE, order preserved)
  // BE already filters out archived courses and returns courseNames in correct order.
  const groupedFiles = (() => {
    const globalFiles = filteredFiles.filter(f => f.scope === 'global' || !f.scope);
    // Use course name as key — BE guarantees courseNames are active & ordered
    const courseGroups = {}; // courseName → { files, order }
    let courseOrder = 0;

    filteredFiles.forEach(f => {
      if (f.scope !== 'course') return;
      const names = f.courseNames || [];
      names.forEach(name => {
        if (!courseGroups[name]) {
          courseGroups[name] = { files: [], order: courseOrder++ };
        }
        courseGroups[name].files.push(f);
      });
    });

    const sections = [];

    if (globalFiles.length > 0) {
      sections.push({
        key: '__global__',
        label: 'Global',
        order: -1,
        icon: <Icon as={Globe} />,
        files: globalFiles,
      });
    }

    Object.entries(courseGroups).forEach(([name, group]) => {
      sections.push({
        key: name,
        label: name,
        order: group.order,
        icon: <Icon as={Book} />,
        files: group.files,
      });
    });

    sections.sort((a, b) => a.order - b.order);

    return sections;
  })();

  const getFileIcon = (fileName) => {
    const fileType = getFileType(fileName);
    const iconMap = {
      pdf: <Icon as={FileText} size={20} style={{ color: 'var(--ant-color-error)' }} />,
      image: <Icon as={FileImage} size={20} style={{ color: 'var(--ant-color-primary)' }} />,
      video: <Icon as={Video} size={20} style={{ color: 'var(--ant-color-info)' }} />,
      audio: <Icon as={Volume2} size={20} style={{ color: 'var(--ant-color-info)' }} />,
      archive: <Icon as={FileArchive} size={20} style={{ color: 'var(--ant-color-warning)' }} />,
      document: <Icon as={FileText} size={20} style={{ color: 'var(--ant-color-primary)' }} />,
      spreadsheet: <Icon as={FileSpreadsheet} size={20} style={{ color: 'var(--ant-color-success)' }} />,
      presentation: <Icon as={Presentation} size={20} style={{ color: 'var(--ant-color-warning)' }} />,
    };
    return iconMap[fileType] || <Icon as={File} size={20} />;
  };

  const handlePreview = (file) => {
    if (canPreviewFile(file.name)) {
      setSelectedFile(file);
      setFileViewerOpen(true);
    }
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatRoleName = (role) => role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 40 }}>
          <Icon as={Folder} style={{ color: 'var(--ant-color-primary)' }} />
          <span>My Files - {formatRoleName(userRole)}</span>
          {!loading && (
            <Tag color="processing" bordered>{filteredFiles.length} files</Tag>
          )}
        </div>
      }
      className="airsmart-dialog"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Close</Button>
          {!loading && !error && (
            <Button onClick={handleRefresh}>Refresh</Button>
          )}
        </div>
      }
      width={768}
      closeIcon={<Icon as={X} />}
      styles={{
        body: { maxHeight: 'calc(80vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
      }}
      destroyOnClose
    >
      {/* Search Bar */}
      <div style={{ marginBottom: 12, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Input
          placeholder="Search by file name, description, course, or heading inside PDF/Word…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          prefix={<Icon as={Search} />}
          allowClear
          suffix={(loading || tocFetching) && searchInput ? <Spin size="small" /> : null}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Select
            value={scopeFilter}
            onChange={setScopeFilter}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All scopes' },
              { value: 'global', label: 'Global' },
              { value: 'course', label: 'Course' },
            ]}
          />
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
        )}

        {!loading && !error && (
          filteredFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Icon
                as={File}
                size={64}
                style={{
                  color: 'var(--ant-color-text-secondary)',
                  marginBottom: 16,
                }}
              />
              <Text type="secondary" strong style={{ display: 'block', marginBottom: 8 }}>
                {searchTerm || scopeFilter !== 'all' ? 'No files found' : 'No files available'}
              </Text>
              <Text type="secondary">
                {searchTerm || scopeFilter !== 'all'
                  ? 'Try a different search or scope filter'
                  : `No files have been uploaded for ${formatRoleName(userRole)} role yet.`}
              </Text>
            </div>
          ) : (
            groupedFiles.map((section, idx) => (
              <div key={section.key}>
                {idx > 0 && <Divider style={{ margin: '8px 0' }} />}
                <div
                  onClick={() => toggleSection(section.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 0 4px',
                    color: section.isDeleted ? 'var(--ant-color-text-tertiary)' : 'var(--ant-color-text-secondary)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}>
                  <Icon
                    as={ChevronRight}
                    size={10}
                    style={{
                      transition: 'transform 0.2s',
                      transform: collapsedSections[section.key] ? 'rotate(0deg)' : 'rotate(90deg)',
                    }}
                  />
                  {section.icon}
                  <span>{section.label}</span>
                  <Tag bordered={false} color="default" style={{ marginLeft: 4 }}>
                    {section.files.length}
                  </Tag>
                </div>
                {!collapsedSections[section.key] && (
                <List
                  dataSource={section.files}
                  split
                  renderItem={(file) => {
                    const tocMatches = tocHitsByKey[file.key];
                    return (
                    <List.Item
                      key={file.key}
                      actions={[
                        ...(canPreviewFile(file.name) ? [
                          <Button
                            key="preview"
                            type="text"
                            shape="circle"
                            icon={<Icon as={Eye} />}
                            onClick={() => handlePreview(file)}
                            title="Preview"
                            size="small"
                          />,
                        ] : []),
                        <Button
                          key="download"
                          type="text"
                          shape="circle"
                          icon={<Icon as={Download} />}
                          onClick={() => handleDownload(file)}
                          title="Download"
                          size="small"
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={getFileIcon(file.name)}
                        title={
                          <Text className="airsmart-dialog-file-name">
                            {file.name}
                          </Text>
                        }
                        description={
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
                            {file.metadata?.description && (
                              <Text type="secondary" style={{ fontSize: 14, fontWeight: 400, lineHeight: '1.6', display: 'block' }}>
                                {file.metadata.description}
                              </Text>
                            )}
                            <div style={{ display: 'flex', gap: 16 }}>
                              <Text type="secondary" style={{ fontSize: 12, opacity: 0.7 }}>
                                {formatFileSize(file.size)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12, opacity: 0.7 }}>
                                {new Date(file.lastModified).toLocaleDateString()}
                              </Text>
                            </div>
                            {file.tags?.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                                {file.tags.map((tag) => (
                                  <Tag
                                    key={tag}
                                    bordered={false}
                                    style={{
                                      margin: 0,
                                      fontSize: 11,
                                      fontWeight: 500,
                                      borderRadius: 4,
                                      backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                                      color: token.colorTextSecondary,
                                    }}
                                  >
                                    {tag}
                                  </Tag>
                                ))}
                              </div>
                            )}
                            {tocMatches?.length > 0 && (
                              <div
                                style={{
                                  marginTop: 6,
                                  padding: '6px 8px',
                                  borderLeft: '2px solid var(--ant-color-primary)',
                                  background: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                  borderRadius: 4,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                }}
                              >
                                <Text type="secondary" style={{ fontSize: 11, fontWeight: 500 }}>
                                  Matched headings ({tocMatches.length})
                                </Text>
                                {tocMatches.slice(0, 5).map((m, i) => (
                                  <div key={`${m.order}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                    <Tag bordered={false} style={{ margin: 0, fontSize: 10 }}>H{m.level}</Tag>
                                    <span style={{ flex: 1, color: 'var(--ant-color-text)' }}>{m.text}</span>
                                    {m.page != null && (
                                      <Text type="secondary" style={{ fontSize: 11 }}>p.{m.page}</Text>
                                    )}
                                  </div>
                                ))}
                                {tocMatches.length > 5 && (
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    +{tocMatches.length - 5} more
                                  </Text>
                                )}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                    );
                  }}
                />
                )}
              </div>
            ))
          )
        )}
      </div>

      <FileViewer
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        file={selectedFile}
      />
    </Modal>
  );
};

export default MyFilesDialog;
